import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, Package, BarChart3, MessageSquare, Users, FileSpreadsheet, Calculator, Blocks, Shield, ArrowRight } from 'lucide-react';

const services = [
  {
    icon: FolderKanban,
    title: 'Project Management Systems',
    description: 'Custom task tracking, team collaboration, and project planning tools tailored to your workflow. Replace Trello, Asana, or Monday with something built for your exact process.',
    features: ['Task Management', 'Team Collaboration', 'Timeline & Gantt Views'],
    prefill: 'Project Management System - Task tracking, team collaboration, and project planning tool',
    size: 'large'
  },
  {
    icon: Package,
    title: 'Inventory & Stock Management',
    description: 'Real-time inventory tracking, automated reordering, and warehouse management systems that eliminate spreadsheet chaos.',
    features: ['Stock Tracking', 'Automated Alerts', 'Multi-location Support'],
    prefill: 'Inventory Management System - Real-time stock tracking with automated reordering',
    size: 'small'
  },
  {
    icon: BarChart3,
    title: 'Sales & CRM Dashboards',
    description: 'Custom CRM systems and sales dashboards that track leads, manage pipelines, and provide real-time insights.',
    features: ['Pipeline Management', 'Sales Analytics', 'Lead Tracking'],
    prefill: 'CRM & Sales Dashboard - Lead tracking, pipeline management, and sales analytics',
    size: 'small'
  },
  {
    icon: Calculator,
    title: 'Accounting & Finance Software',
    description: 'Custom invoicing, expense tracking, payroll processing, and financial reporting systems tailored to your business needs.',
    features: ['Invoicing', 'Expense Tracking', 'Financial Reports'],
    prefill: 'Accounting Software - Custom invoicing, expense tracking, and financial reporting',
    size: 'large'
  },
  {
    icon: FileSpreadsheet,
    title: 'Reporting & Analytics',
    description: 'Transform your scattered data into actionable insights with custom dashboards and automated reporting systems.',
    features: ['Custom Dashboards', 'Automated Reports', 'Data Visualization'],
    prefill: 'Reporting & Analytics Dashboard - Custom data visualization and automated reporting',
    size: 'small'
  },
  {
    icon: Users,
    title: 'HR & Employee Systems',
    description: 'Internal HR platforms for employee records, leave management, onboarding, and performance tracking.',
    features: ['Employee Records', 'Leave Management', 'Performance Reviews'],
    prefill: 'HR System - Employee records, leave management, and performance tracking',
    size: 'small'
  },
  {
    icon: Shield,
    title: 'Encrypted Databases & Vaults',
    description: 'Secure internal databases and encrypted vaults for sensitive data. Enterprise-grade security with role-based access control.',
    features: ['End-to-End Encryption', 'Access Control', 'Audit Logs'],
    prefill: 'Encrypted Database/Vault - Secure storage with role-based access control',
    size: 'small'
  },
  {
    icon: MessageSquare,
    title: 'Internal Communication Tools',
    description: 'Secure internal chat, notifications, and collaboration tools that keep your team connected.',
    features: ['Team Messaging', 'Notifications', 'File Sharing'],
    prefill: 'Internal Communication Tool - Secure team messaging and collaboration',
    size: 'small'
  },
  {
    icon: Blocks,
    title: 'Custom Software Solutions',
    description: 'Bespoke software built from scratch to solve your unique business challenges. If you can dream it, we can build it.',
    features: ['Tailored Solutions', 'API Integrations', 'Scalable Architecture'],
    prefill: 'Custom Software Solution - Bespoke system for unique business requirements',
    size: 'large'
  }
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = service.icon;
  
  // Create URL with pre-filled project description
  const quoteUrl = `/instant-quote?project=${encodeURIComponent(service.prefill)}`;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className={`${service.size === 'large' ? 'md:col-span-2' : 'md:col-span-1'} 
        bg-white border border-slate-100 hover:border-[#003333]/20 
        transition-all duration-500 p-6 md:p-8 flex flex-col justify-between h-full group card-hover`}
      data-testid={`service-card-${index}`}
    >
      <div>
        <div className="w-14 h-14 bg-[#F8FAFC] rounded-xl flex items-center justify-center mb-6 text-[#003333] group-hover:bg-[#003333] group-hover:text-white transition-all duration-300">
          <Icon className="w-7 h-7" />
        </div>
        
        <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-[#003333] mb-3">
          {service.title}
        </h3>
        
        <p className="text-sm md:text-base leading-relaxed text-slate-600 mb-6">
          {service.description}
        </p>
      </div>
      
      <div>
        <div className="flex flex-wrap gap-2 mb-6">
          {service.features.map((feature, i) => (
            <span 
              key={i}
              className="px-3 py-1 bg-[#F8FAFC] text-xs md:text-sm text-[#003333]/70 rounded-full"
            >
              {feature}
            </span>
          ))}
        </div>
        
        <Link 
          to={quoteUrl}
          className="inline-flex items-center gap-2 text-[#003333] font-medium text-sm group-hover:gap-4 transition-all hover:text-[#004d4d]"
          data-testid={`service-quote-link-${index}`}
        >
          <span>Get a quote</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
};

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="services" className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            What We Build
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#003333] max-w-4xl mb-6">
            Internal Systems That Drive Growth
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl">
            Replace spreadsheets, fragile workflows, and disconnected tools with custom software built around how your business actually works.
          </p>
        </motion.div>
        
        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
